import type { Messages } from './en.js';

export const es: Messages = {
  app: {
    name: 'Vammo Work Tracker',
    fixtureBanner: 'Entorno de fixtures — datos de ejemplo, sin integraciones reales y sin mensajes reales.',
    skipToContent: 'Ir al contenido',
  },
  nav: {
    myWork: 'Mi trabajo',
    requests: 'Solicitudes',
    work: 'Trabajo',
    projects: 'Proyectos',
    teams: 'Equipos',
    reports: 'Reportes',
    admin: 'Administración',
    settings: 'Configuración',
    newRequest: 'Nueva solicitud',
    newWork: 'Nuevo ítem de trabajo',
    search: 'Buscar',
    searchPlaceholder: 'Busca trabajo y solicitudes o escribe una clave…',
    notifications: 'Notificaciones',
    collapse: 'Contraer menú',
    expand: 'Expandir menú',
    openMenu: 'Abrir navegación',
    signOut: 'Cerrar sesión',
    theme: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    themeSystem: 'Sistema',
    language: 'Idioma',
    shortcuts: 'Atajos de teclado',
    tutorial: 'Tutorial',
  },
  repoPicker: {
    workTitle: 'Repositorios vinculados',
    workHint: 'Vincular repositorios agrega evidencia para el trabajo futuro. Los episodios completados conservan su base original.',
    projectTitle: 'Repositorios del proyecto',
    projectHint: 'La reconciliación y la cobertura de captura incluyen los repositorios adjuntos aquí.',
    search: 'Buscar repositorios',
    noneSelected: 'No hay repositorios seleccionados.',
    remove: 'Quitar {{name}}',
    archived: 'Archivado',
    edit: 'Editar repositorios',
  },
  translationReport: {
    action: 'Reportar',
    title: 'Reportar esta traducción',
    hint: 'Cuéntanos qué está mal. El texto original nunca se modifica.',
    comment: '¿Qué está mal? (opcional)',
    regenerate: 'Traducir de nuevo',
    submit: 'Enviar reporte',
    thanks: 'Gracias, se registró el reporte',
  },
  languageCorrection: {
    label: 'Idioma original',
    hint: 'Corrígelo si el texto se detectó en el idioma equivocado; las traducciones se recrean.',
  },
  conflict: {
    title: 'Alguien guardó antes',
    hint: 'Compara tu cambio con la versión actual y elige cuál conservar.',
    field: 'Campo',
    mine: 'Tu cambio',
    current: 'Versión actual',
    applyMine: 'Aplicar mi cambio',
    keepCurrent: 'Conservar la versión actual',
    fields: { title: 'Título', descriptionMarkdown: 'Descripción', acceptanceCriteria: 'Criterios de aceptación', priority: 'Prioridad', assigneeId: 'Responsable', labels: 'Etiquetas', type: 'Tipo', originalLocale: 'Idioma original', repositoryIds: 'Repositorios', problemMarkdown: 'Problema', expectedOutcome: 'Resultado esperado' },
  },
  common: {
    removeItem: 'Quitar {{name}}',
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    close: 'Cerrar',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    archive: 'Archivar',
    confirm: 'Confirmar',
    submit: 'Enviar',
    back: 'Volver',
    next: 'Siguiente',
    retry: 'Reintentar',
    reload: 'Recargar',
    loading: 'Cargando…',
    loadMore: 'Cargar más',
    none: 'Ninguno',
    unassigned: 'Sin asignar',
    unknown: 'Desconocido',
    optional: 'opcional',
    required: 'Obligatorio',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    me: 'Yo',
    filter: 'Filtrar',
    clearFilters: 'Limpiar filtros',
    sort: 'Ordenar',
    openFullPage: 'Abrir página completa',
    copyLink: 'Copiar enlace',
    linkCopied: 'Enlace copiado',
    viewOriginal: 'Ver original',
    viewTranslation: 'Ver traducción',
    machineTranslated: 'Traducción automática del {{language}}',
    translating: 'Traduciendo…',
    translationFailed: 'Traducción no disponible; se muestra el original.',
    reportTranslation: 'Reportar traducción',
    noResults: 'Sin resultados',
    comingSoon: 'Aún no disponible',
    reason: 'Motivo',
    reasonPlaceholder: 'Explica la decisión',
    created: 'Creado',
    updated: 'Actualizado',
    by: 'por {{name}}',
    you: 'tú',
    changesSaved: 'Cambios guardados',
    actionFailed: 'La acción falló',
    count_one: '{{count}} elemento',
    count_other: '{{count}} elementos',
    preview: 'Vista previa',
    write: 'Escribir',
    markdownHint: 'Admite Markdown: **negrita**, _cursiva_, `código`, listas y enlaces.',
    unsavedChanges: 'Tienes cambios sin guardar. Guárdalos o cancélalos primero.',
    discard: 'Descartar',
    keepEditing: 'Seguir editando',
  },
  errors: {
    title: 'Algo salió mal',
    notFound: 'No se encontró, o no tienes acceso.',
    forbidden: 'No tienes permiso para hacer eso.',
    unauthenticated: 'Tu sesión terminó. Inicia sesión de nuevo para continuar.',
    conflict: 'Otra persona cambió esto mientras tanto. Revisa la versión más reciente y vuelve a aplicar tu edición.',
    staleGeneration: 'Existe una entrega más reciente. Revisa el estado actual antes de responder.',
    rateLimited: 'Demasiadas solicitudes. Espera un momento.',
    unavailable: 'Esta funcionalidad no está disponible en este momento.',
    offline: 'Parece que no tienes conexión. Los cambios no se guardarán hasta que vuelva.',
    validation: 'Corrige los campos resaltados.',
    generic: 'Error inesperado. Referencia: {{correlationId}}',
  },
  auth: {
    signIn: 'Iniciar sesión con Vammo',
    signingIn: 'Redirigiendo al inicio de sesión…',
    errorTitle: 'El inicio de sesión no se completó',
    errorState: 'El enlace de inicio de sesión expiró o ya se usó. Empieza de nuevo.',
    errorSuspended: 'Tu cuenta está suspendida. Contacta a un administrador.',
    errorGeneric: 'No pudimos verificar tu inicio de sesión corporativo.',
    tryAgain: 'Reintentar',
  },
  status: {
    request: {
      DRAFT: 'Borrador',
      SUBMITTED: 'Enviada',
      NEEDS_INFORMATION: 'Requiere información',
      ACCEPTED: 'Aceptada',
      REJECTED: 'Rechazada',
      DUPLICATE: 'Duplicada',
      RESOLVED: 'Resuelta',
      NEEDS_ATTENTION: 'Requiere atención',
      CANCELED: 'Cancelada',
    },
    phase: {
      TODO: 'Por hacer',
      IN_PROGRESS: 'En curso',
      IN_REVIEW: 'En revisión',
      INTEGRATED: 'Integrado',
      DONE: 'Terminado',
      CANCELED: 'Cancelado',
    },
    priority: { URGENT: 'Urgente', HIGH: 'Alta', NORMAL: 'Normal', LOW: 'Baja' },
    workType: {
      FEATURE: 'Funcionalidad',
      BUG: 'Bug',
      MAINTENANCE: 'Mantenimiento',
      INFRASTRUCTURE: 'Infraestructura',
      RESEARCH: 'Investigación',
      ANALYSIS: 'Análisis',
      OPERATIONS: 'Operaciones',
      REVIEW: 'Revisión',
      OTHER: 'Otro',
    },
    policy: { PRODUCTION: 'Entrega en producción', DELIVERABLE: 'Entregable', REVIEWED_DELIVERABLE: 'Entregable revisado' },
    policyHelp: {
      PRODUCTION: 'Terminado solo cuando cada cambio requerido está verificado en producción.',
      DELIVERABLE: 'Terminado cuando la persona responsable registra el resultado frente a los criterios.',
      REVIEWED_DELIVERABLE: 'Terminado cuando un revisor independiente aprueba el resultado.',
    },
    delivery: { UNKNOWN: 'Entrega desconocida', NOT_DEPLOYED: 'No desplegado', PARTIAL: 'Desplegado parcialmente', DEPLOYED: 'Desplegado', REVERTED: 'Revertido' },
    association: { CONFIRMED: 'Confirmado', PROVISIONAL: 'Provisional' },
    visibility: { COMPANY: 'Empresa', RESTRICTED: 'Restringido' },
    visibilityHelp: { COMPANY: 'Visible para todas las personas de Vammo con sesión iniciada.', RESTRICTED: 'Visible solo para las personas, equipos y grupos seleccionados (y administradores).' },
    basis: { AGENT_REPORTED: 'Reportado por agente', HUMAN_REPORTED: 'Reportado', PROVIDER_VERIFIED: 'Verificado', IMPORTED_SOURCE: 'Importado', FIXTURE: 'Fixture' },
    completionBasis: { VERIFIED_PRODUCTION: 'Verificado en producción', DELIVERABLE: 'Entregable registrado', REVIEWED_DELIVERABLE: 'Revisado y aprobado', IMPORTED_SOURCE: 'Terminado en Jira (importado)' },
    confirmation: { UNCONFIRMED: 'Esperando tu confirmación', CONFIRMED: 'Confirmado como resuelto', PERSISTS: 'Sigue ocurriendo' },
    fulfillment: {
      NOT_STARTED: 'Sin empezar',
      IN_PROGRESS: 'En curso',
      IN_REVIEW: 'En revisión',
      AWAITING_DEPLOYMENT: 'Esperando despliegue en producción',
      AWAITING_ACTIVATION: 'Esperando activación',
      DELIVERED: 'Entregado',
      NONE_LINKED: 'Aún no planificado',
    },
    channel: { WEB: 'Web', SLACK: 'Slack', MCP: 'Asistente de IA', API: 'API', EMAIL: 'Correo', JIRA_IMPORT: 'Importación de Jira', SYSTEM: 'Sistema' },
    scope: { NONE: 'No aplica', AWAITING_SCOPE: 'Esperando alcance de entrega', UNCONFIRMED: 'Alcance sin confirmar', CONFIRMED: 'Alcance confirmado' },
    milestone: { START: 'Iniciado', SCOPE_CHANGE: 'Cambio de alcance', BLOCKER: 'Bloqueo', VERIFICATION: 'Verificación', PR_UPDATE: 'Pull request', HANDOFF: 'Traspaso', SESSION_END: 'Sesión finalizada', NOTE: 'Nota' },
    client: { CLAUDE_CODE: 'Claude Code', CODEX: 'Codex', CLAUDE: 'Claude', CHATGPT: 'ChatGPT', OTHER: 'Otro cliente' },
  },
  actions: {
    request: {
      submit: 'Enviar solicitud',
      cancel: 'Cancelar solicitud',
      ask_information: 'Pedir información',
      answer_information: 'Enviar respuesta',
      accept: 'Aceptar',
      reject: 'Rechazar',
      mark_duplicate: 'Marcar como duplicada',
      fulfill: 'Marcar como atendida',
      report_persistence: 'Informar que sigue ocurriendo',
      fulfillment_invalidated: 'Reabrir para atención',
      accept_correction: 'Aceptar corrección',
      fulfill_after_review: 'Resolver tras revisión',
      reconsider: 'Reconsiderar',
    },
    work: {
      start: 'Empezar',
      request_review: 'Pedir revisión',
      request_changes: 'Pedir cambios',
      integration_observed: 'Integrado',
      production_verified: 'Verificado en producción',
      complete_deliverable: 'Completar',
      approve_deliverable: 'Aprobar',
      cancel: 'Cancelar trabajo',
      reopen: 'Reabrir',
      coverage_lost: 'Cobertura perdida',
    },
  },
  myWork: {
    title: 'Mi trabajo',
    greeting: 'Hola, {{name}}',
    subtitle: 'Lo que necesita tu atención hoy.',
    assigned: 'Asignado a mí',
    assignedEmpty: 'No tienes nada asignado por ahora.',
    myRequests: 'Mis solicitudes',
    myRequestsEmpty: 'Todavía no registraste solicitudes.',
    needsMe: 'Esperándome',
    needsMeEmpty: 'Nada te está esperando.',
    blocked: 'Bloqueado',
    triageInbox: 'Bandeja de triage',
    updates: 'Novedades recientes',
    updatesEmpty: 'Todavía no hay novedades.',
    following: 'Siguiendo',
    followingEmpty: 'Sigue trabajo o solicitudes para verlos aquí.',
    stats: { open: 'Trabajo abierto', inReview: 'En revisión', blocked: 'Bloqueados', attention: 'Solicitudes que requieren atención' },
  },
  requests: {
    move: { noProject: 'Sin proyecto', title: 'Mover a {{name}}', hint: 'Mover cambia quién puede leer esta solicitud, sus comentarios y archivos.', restrictedAfter: 'Tras moverla, la solicitud quedará restringida a la audiencia del proyecto.', companyAfter: 'Tras moverla, la solicitud será visible para toda la empresa.', losesAccess: 'Estas personas perderían acceso:', nobodyLoses: 'Nadie involucrado pierde acceso.', reassignFirst: 'El responsable perdería acceso. Reasigna la solicitud primero.', confirm: 'Mover solicitud' },
    activation: { pending: 'Activación pendiente:', observed: 'Activación observada por {{name}} el {{time}}', notRequired: 'No requiere una activación aparte.', require: 'Requerir activación', record: 'Registrar activación', waive: 'Eximir activación', requireHint: 'Úsalo cuando desplegar no alcanza, por ejemplo si hay que encender un feature flag. La solicitud queda aceptada hasta registrar la activación.', recordHint: 'Registra quién observó el cambio activo y la evidencia. Queda auditado.', waiveHint: 'La activación dejará de ser obligatoria. El motivo queda auditado.', noteLabel: '¿Qué debe activarse?', evidenceLabel: 'Evidencia', reasonLabel: 'Motivo' },
    duplicates: { title: 'Posibles duplicados', hint: 'Solicitudes similares que puedes ver. Al marcarla, cada solicitante sigue informado.', mark: 'Marcar como duplicada de esta' },
    title: 'Solicitudes',
    subtitle: 'Problemas, ideas y pedidos de toda Vammo.',
    tabs: { inbox: 'Entrada', mine: 'Mías', attention: 'Requieren atención', following: 'Siguiendo', all: 'Todas' },
    empty: 'No hay solicitudes aquí.',
    emptyInbox: 'La bandeja está vacía. Las nuevas solicitudes aparecen aquí para triage.',
    columns: { key: 'Clave', title: 'Título', status: 'Estado', priority: 'Prioridad', author: 'Solicitante', assignee: 'Responsable', team: 'Equipo', updated: 'Actualizado', source: 'Origen' },
    detail: {
      problem: 'Problema',
      currentBehavior: 'Qué pasa ahora',
      expectedOutcome: 'Resultado esperado',
      impact: 'Impacto',
      impactUnknown: 'Impacto aún desconocido',
      reproduction: 'Cómo reproducirlo',
      affectedArea: 'Área afectada',
      progress: 'Progreso',
      decisions: 'Historial de decisiones',
      comments: 'Comentarios',
      linkedWork: 'Trabajo vinculado',
      linkedWorkEmpty: 'Todavía no hay trabajo vinculado.',
      required: 'Requerido',
      optional: 'Opcional',
      hiddenWork: 'Parte del trabajo vinculado es restringido y no se muestra.',
      pendingQuestion: 'El equipo preguntó',
      answerPlaceholder: 'Responde la pregunta…',
      duplicateOf: 'Duplicada de',
      decisionReason: 'Nota de la decisión',
      source: 'Origen',
      triage: 'Triage',
      triageTeam: 'Equipo de triage',
      followers: 'Seguir',
      following: 'Siguiendo',
      linkWork: 'Vincular trabajo',
      linkWorkSearch: 'Busca trabajo por clave o título',
      unlink: 'Desvincular',
    },
    confirm: {
      fromEmail: 'Elige tu respuesta abajo. No se registra nada hasta que hagas clic.',
      title: '¿Esto quedó resuelto para ti?',
      body: 'El equipo marcó esta solicitud como resuelta. Cuéntales si te funciona.',
      confirmed: 'Sí, está resuelto',
      persists: 'No, sigue ocurriendo',
      commentPlaceholder: 'Agrega detalles (opcional)',
      thanks: 'Gracias por confirmar.',
      reported: 'Gracias — el equipo lo revisará de nuevo.',
      generation: 'Entrega n.º {{generation}}',
    },
    dialogs: {
      rejectTitle: 'Rechazar solicitud',
      duplicateTitle: 'Marcar como duplicada',
      duplicateSearch: 'Busca la solicitud principal',
      askTitle: 'Pedir información',
      askPlaceholder: '¿Qué necesitas saber?',
      fulfillTitle: 'Marcar como atendida',
      fulfillDirect: 'Describe el resultado entregado',
      reviewTitle: 'Resolver tras revisión',
      reviewPlaceholder: 'Explica por qué el resultado actual ya atiende la solicitud',
      correctionTitle: 'Aceptar corrección',
      correctionWork: 'Ítem de trabajo correctivo (opcional)',
      cancelTitle: 'Cancelar solicitud',
      reconsiderTitle: 'Reconsiderar solicitud',
    },
  },
  intake: {
    title: 'Nueva solicitud',
    subtitle: 'Describe lo que está pasando. No necesitas saber cómo arreglarlo.',
    tabs: { assistant: 'Guiado', form: 'Formulario' },
    assistantIntro: 'Cuéntame el problema o la idea con tus palabras. Solo te preguntaré lo que falte.',
    assistantPlaceholder: 'Escribe tu respuesta…',
    assistantUnavailable: 'El asistente no está disponible ahora. Usa el formulario; todo se guarda.',
    assistant: {
      ack: 'Gracias, lo guardé en el borrador.',
      askProblem: '¿Qué está pasando y dónde?',
      askExpected: '¿Qué debería pasar en cambio?',
      askImpact: '¿A quién afecta y cuánto importa? Está bien decir que no lo sabés.',
      askReproduction: 'Si es un error: ¿cómo se puede ver que ocurre y con qué frecuencia?',
      ready: 'Ya tengo lo que necesita triage. Revisá el resumen y envialo cuando esté bien.',
      fallback: 'El asistente no está disponible, así que guardé tu respuesta tal cual.',
    },
    fields: {
      title: 'Título corto',
      titlePlaceholder: 'p. ej.: La app no muestra el historial de intercambios',
      problem: '¿Cuál es el problema?',
      problemPlaceholder: 'Describe la situación y a quién afecta',
      currentBehavior: '¿Qué pasa ahora?',
      expectedOutcome: '¿Qué debería pasar?',
      impact: 'Impacto',
      impactPlaceholder: '¿A quién o qué afecta, y cuánto?',
      impactUnknown: 'Todavía no conozco el impacto',
      reproduction: 'Pasos para reproducirlo (para bugs)',
      affectedArea: 'Área o producto afectado',
      language: 'Escrito en',
      visibility: 'Quién puede verlo',
      team: 'Enviar al equipo',
      teamAuto: 'Que decida el triage',
      priority: 'Prioridad',
      evidence: 'Adjuntos',
    },
    duplicates: 'Solicitudes posiblemente relacionadas',
    duplicatesHelp: 'Revisa si alguna ya cubre tu solicitud.',
    review: 'Revisar y enviar',
    reviewHelp: 'Revisa el resumen. Todavía puedes editar antes de enviar.',
    missing: 'Falta: {{fields}}',
    saved: 'Borrador guardado',
    submitted: 'Solicitud {{key}} enviada',
    draftNote: 'Los borradores son privados hasta que los envíes.',
  },
  work: {
    title: 'Trabajo',
    subtitle: 'Todo lo que está en curso entre equipos, proyectos y repositorios.',
    views: { list: 'Lista', board: 'Tablero' },
    empty: 'Ningún trabajo coincide con estos filtros.',
    columns: { key: 'Clave', title: 'Título', phase: 'Fase', priority: 'Prioridad', type: 'Tipo', assignee: 'Responsable', project: 'Proyecto', team: 'Equipo', updated: 'Actualizado', delivery: 'Entrega' },
    filters: { phase: 'Fase', type: 'Tipo', priority: 'Prioridad', assignee: 'Responsable', project: 'Proyecto', team: 'Equipo', blocked: 'Solo bloqueados', provisional: 'Solo provisionales', includeCanceled: 'Incluir cancelados', label: 'Etiqueta' },
    bulk: { selected: '{{count}} seleccionados', setPriority: 'Definir prioridad', assign: 'Asignar', addLabel: 'Agregar etiqueta', clear: 'Limpiar selección' },
    board: { moveTo: 'Mover a…', dropRejected: 'Ese movimiento no está permitido: {{reason}}', productionHint: 'El trabajo de producción solo queda Terminado con despliegues verificados.' },
    create: {
      title: 'Nuevo ítem de trabajo',
      responsibility: 'Responsable',
      team: 'Equipo',
      personal: 'Personal (yo)',
      project: 'Proyecto',
      noProject: 'Sin proyecto',
      type: 'Tipo',
      policy: 'Finalización',
      description: 'Descripción',
      criteria: 'Criterios de aceptación',
      addCriterion: 'Agregar criterio',
      criterionPlaceholder: 'Resultado observable…',
      assignee: 'Responsable',
      labels: 'Etiquetas',
      labelsPlaceholder: 'separadas, por comas',
      created: '{{key}} creado',
    },
    provisional: {
      confirm: 'Confirmar como trabajo nuevo',
      merge: 'Fusionar con trabajo existente',
      mergeTitle: 'Fusionar con trabajo existente',
      mergeHint: 'La clave pasa a ser un alias del ítem elegido. Contribuciones, sesiones, pull requests y vínculos con solicitudes se trasladan; no se borra nada.',
      mergeInto: 'Fusionar con {{key}}',
      confirmed: 'Confirmado como trabajo nuevo',
      merged: 'Fusionado con {{key}}',
    },
    detail: {
      overview: 'Resumen',
      activity: 'Actividad',
      progress: 'Progreso',
      delivery: 'Entrega',
      code: 'Código',
      codeTab: {
        pullRequests: 'Pull requests',
        commits: 'Commits',
        evidence: 'Links reportados por agentes',
        noPullRequests: 'No hay pull requests vinculados.',
        noCommits: 'No hay commits vinculados.',
        linkLabel: 'Vincular un commit o pull request',
        linkHint: 'Pega su URL de GitHub. Los commits y pull requests que mencionan {{key}} se vinculan solos.',
        link: 'Vincular',
        linkedCommit: 'Commit vinculado',
        linkedPr: 'Pull request vinculado',
        unlink: 'Desvincular',
        unlinked: 'Vínculo quitado',
        viaKey: 'Por clave',
        manual: 'Vinculado a mano',
        prState: {
          OPEN: 'Abierto',
          CLOSED: 'Cerrado',
          MERGED: 'Mergeado',
        },
        errors: {
          not_connected: 'Ese repositorio no está conectado. Instala primero la GitHub App en él.',
          not_synced: 'Ese pull request todavía no se sincronizó. Asocia el repositorio a un proyecto y vuelve a intentar en unos minutos.',
          not_found: 'No existe un commit con ese SHA en el repositorio.',
          unsupported: 'Pega la URL de un commit o pull request de GitHub.',
          invalid_format: 'Pega la URL de un commit o pull request de GitHub.',
        },
      },
      history: 'Historial',
      description: 'Descripción',
      noDescription: 'Todavía sin descripción.',
      criteria: 'Criterios de aceptación',
      noCriteria: 'Sin criterios de aceptación.',
      properties: 'Propiedades',
      owner: 'Dueño',
      reviewer: 'Revisor',
      repositories: 'Repositorios',
      noRepositories: 'Sin repositorios — está bien para trabajo sin código.',
      labels: 'Etiquetas',
      blockers: 'Bloqueos',
      addBlocker: 'Agregar bloqueo',
      blockerReason: '¿Qué lo está bloqueando?',
      preventsCompletion: 'Impide terminar',
      resolveBlocker: 'Resolver',
      relations: 'Trabajo relacionado',
      addRelation: 'Vincular trabajo',
      relationKinds: { PARENT_OF: 'Padre de', DEPENDS_ON: 'Depende de', RELATES_TO: 'Relacionado con', DUPLICATES: 'Duplica' },
      requiresCompletion: 'Requerido para terminar',
      contributors: 'Contribuidores',
      linkedRequests: 'Solicitudes',
      outcome: 'Resultado',
      outcomePlaceholder: '¿Qué se entregó?',
      complete: 'Completar entregable',
      criterionStatus: { OPEN: 'Abierto', MET: 'Cumplido', NOT_MET: 'No cumplido', WAIVED: 'Dispensado' },
      episodes: 'Episodios de finalización',
      provisionalNote: 'Creado de forma provisional por un agente; confírmalo o fusiónalo.',
      blockedNote: 'Bloqueado',
      awaitingDeployment: 'Esperando verificación del despliegue',
      sessions: 'Sesiones de agentes',
      evidence: 'Evidencia',
      noEvidence: 'No hay evidencia adjunta.',
      timelineEmpty: 'Todavía sin historial.',
    },
  },
  projects: {
    addManager: 'Agregar responsable',
    addTeam: 'Agregar equipo responsable',
    archive: 'Archivar este proyecto',
    archiveHint: 'Los proyectos archivados salen de listas y selectores; su trabajo e historial siguen visibles.',
    title: 'Proyectos',
    subtitle: 'Los proyectos agrupan trabajo; los repositorios son opcionales.',
    new: 'Nuevo proyecto',
    empty: 'Todavía no hay proyectos.',
    key: 'Clave',
    name: 'Nombre',
    description: 'Descripción',
    managers: 'Responsables',
    owningTeams: 'Equipos dueños',
    openWork: '{{count}} abiertos',
    defaultPolicy: 'Finalización por defecto',
    requiresReview: 'Exigir revisión independiente para entregables',
    settings: 'Configuración',
    visibility: 'Visibilidad',
    repositories: 'Repositorios',
  },
  teams: {
    title: 'Equipos',
    members: 'Miembros',
    manager: 'Responsable',
    member: 'Miembro',
    addMember: 'Agregar miembro',
    remove: 'Quitar',
    new: 'Nuevo equipo',
    slug: 'Identificador',
  },
  visibility: {
    title: 'Visibilidad',
    grants: 'Personas, equipos y grupos',
    addGrant: 'Agregar acceso',
    searchPrincipal: 'Busca personas, equipos o grupos',
    inherit: 'Las restricciones de los proyectos padre siempre se aplican.',
    changed: 'Visibilidad actualizada',
  },
  settings: {
    title: 'Configuración',
    profile: 'Perfil',
    displayName: 'Nombre visible',
    language: 'Idioma',
    timezone: 'Zona horaria',
    notifications: 'Notificaciones',
    identities: 'Cuentas vinculadas',
    noIdentities: 'No hay cuentas vinculadas.',
    roles: 'Acceso',
    basic: 'Acceso básico',
    github: {
      addLabel: 'Agregar una cuenta de GitHub',
      hint: 'Tu usuario de GitHub, por ejemplo una cuenta personal con la que a veces haces commits. Las cuentas que hacen commits con tu email de Vammo se vinculan solas.',
      placeholder: 'usuario',
      add: 'Agregar',
      added: 'Cuenta de GitHub agregada',
      remove: 'Quitar',
      removed: 'Cuenta de GitHub quitada',
      confirmRemove: '¿Quitar esta cuenta?',
      method: { self: 'Agregada por ti', email: 'Verificada por email de commit', admin: 'Vinculada por un administrador', other: 'Vinculada' },
      errors: {
        notFound: 'No existe una cuenta de GitHub con ese usuario.',
        notUser: 'Solo se pueden agregar cuentas personales de GitHub, no organizaciones ni bots.',
        taken: 'Esta cuenta de GitHub ya está vinculada a otra persona. Pídeselo a un administrador.',
        tooMany: 'Ya tienes la cantidad máxima de cuentas de GitHub.',
        unavailable: 'No se pudo contactar a GitHub. Inténtalo de nuevo en un momento.',
      },
    },
  },
  admin: {
    title: 'Administración',
    users: 'Usuarios y roles',
    groups: 'Grupos de visibilidad',
    integrations: 'Integraciones',
    exceptions: 'Excepciones',
    imports: 'Importación de Jira',
    audit: 'Registro de auditoría',
    operations: 'Operaciones',
    roles: 'Roles',
    capabilities: 'Capacidades',
    status: 'Estado',
    suspend: 'Suspender',
    reactivate: 'Reactivar',
    lastLogin: 'Último inicio de sesión',
    changeAccess: 'Cambiar acceso',
    reasonRequired: 'Se registra un motivo en el log de auditoría.',
    reason: { title: 'Registrar un motivo', label: 'Motivo', hint: 'El motivo queda en el registro de auditoría.', confirm: 'Confirmar' },
    integ: { lastSuccess: 'Último éxito', lastAttempt: 'Último intento', lastError: 'Último error', circuit: 'Circuito abierto hasta', cursor: 'Cursor', probe: 'Probar', reconcile: 'Reconciliar', reconcileQueued: 'Reconciliación encolada', liveVerified: 'Verificado en vivo', health: { HEALTHY: 'Saludable', DEGRADED: 'Degradado', FAILING: 'Fallando', DISABLED: 'Desactivado', UNVERIFIED: 'Sin verificar' }, modes: { live: 'En vivo', fixture: 'Fixture', disabled: 'Desactivado' } },
    deliveries: { title: 'Entregas de notificaciones', hint: 'Un envío incierto puede haber llegado ya a la persona. Revisa la evidencia del proveedor antes de reintentar.', filter: 'Estado de entrega', filters: { DELIVERY_UNKNOWN: 'Inciertas o fallidas', BOUNCED: 'Rebotadas o suprimidas', SENT: 'Enviadas', ALL: 'Todas' }, empty: 'Ninguna entrega requiere atención.', attempts: '{{count}} intento(s)', retry: 'Reintentar', retryTitle: 'Reintentar esta entrega', retryHint: 'El mensaje puede enviarse de nuevo. El motivo queda en el registro de auditoría.', retried: 'Reintento en cola' },
    services: { title: 'Servicios y entornos', hint: 'Aprueba qué repositorios construyen cada servicio y qué entornos son producción. La cobertura solo usa mapeos aprobados.', empty: 'Todavía no se descubrieron servicios.', repositories: 'Repositorios', environments: 'Entornos', buildPath: 'Ruta de build (opcional)', buildPathFor: 'Ruta de build de {{name}}', classificationFor: 'Clasificación de {{name}}', noRepositories: 'Sin repositorios mapeados.', approve: 'Aprobar mapeo', approved: 'Mapeo aprobado', classes: { PRODUCTION: 'Producción', STAGING: 'Staging', DEVELOPMENT: 'Desarrollo', PREVIEW: 'Preview', OTHER: 'Otro' } },
    identities: { action: 'Cuentas vinculadas', title: 'Cuentas vinculadas', hint: 'Vincula una cuenta de GitHub o Jira solo cuando hayas confirmado que pertenece a esta persona. Los vínculos se auditan.', none: 'Sin cuentas vinculadas.', add: 'Vincular una cuenta', provider: 'Proveedor', search: 'Buscar por usuario o ID', candidates: 'Cuentas que se pueden vincular', historical: 'Historial de Jira', unmapped: 'Vista {{count}} vez/veces', noCandidates: 'No hay cuentas sin vincular.', link: 'Vincular cuenta', linked: 'Cuenta vinculada', unlink: 'Desvincular' },
    exc: { empty: 'No hay excepciones abiertas.', resolve: 'Resolver', ignore: 'Ignorar', open: 'Abrir', occurrences: '{{count}} ocurrencia(s)', filterKind: 'Tipo', all: 'Todos los tipos', kinds: { UNMATCHED_WORK: 'Trabajo sin asociar', UNCERTAIN_ASSOCIATION: 'Asociación incierta', UNMAPPED_IDENTITY: 'Identidad sin mapear', UNMAPPED_REPOSITORY: 'Repositorio sin mapear', UNMAPPED_SERVICE: 'Servicio sin mapear', CONNECTOR_FAILURE: 'Falla de conector', DUPLICATE_CORRECTION: 'Corrección de duplicado', WEBHOOK_INTEGRITY: 'Integridad de webhook', DEPLOYMENT_ORDERING: 'Orden de despliegues', DELIVERY_UNKNOWN: 'Entrega incierta', IMPORT_EXCEPTION: 'Excepción de importación', ACL_QUARANTINE: 'Cuarentena de acceso', SCOPE_REVIEW: 'Revisión de alcance', COMPLETION_BLOCKED: 'Finalización bloqueada' } },
    ops: { noAlerts: 'Sin alertas activas', outbox: 'Outbox pendiente', worker: 'Worker', healthy: 'Saludable', stale: 'Sin latido', aiTokens: 'Tokens de IA hoy', queue: 'Cola', queued: 'En cola', active: 'Activos', oldest: 'Espera más antigua', dead: 'Dead letters', replay: 'Reintentar', deadEmpty: 'No hay jobs en dead letter.' },
    imp: { mode: 'Modo', projects: 'Claves de proyecto (separadas por coma; vacío usa los proyectos aprobados)', start: 'Iniciar', queued: 'Importación encolada', resume: 'Reanudar', cancel: 'Cancelar', empty: 'Todavía no hay ejecuciones de importación.', exceptions: '{{count}} excepción(es)', mapping: 'Mapeos', mappingHelp: 'Primero ejecutá el descubrimiento. Aprobá cada proyecto antes de importar muestra, completo o delta. Los niveles de seguridad sin mapear quedan en cuarentena para administradores.', save: 'Guardar mapeos', saved: 'Mapeos guardados', approved: 'Aprobado', targetProject: 'Proyecto destino', targetTeam: 'Equipo de triage/responsable', none: 'Ninguno', requestTypes: 'Tipos importados como solicitudes', statusMap: 'Mapeo de estados', securityLevels: 'Niveles de seguridad', securityGroup: 'Visible para el grupo', quarantine: 'Cuarentena (solo administradores)', reconciliation: 'Reconciliación', modes: { DISCOVERY: 'Descubrimiento', DRY_RUN: 'Simulación', SAMPLE: 'Muestra', FULL: 'Importación completa', DELTA: 'Delta', RECONCILE: 'Reconciliar' }, statuses: { QUEUED: 'En cola', RUNNING: 'En curso', PAUSED: 'Pausada', SUCCEEDED: 'Completada', FAILED: 'Fallida', CANCELED: 'Cancelada' } },
    slackDest: { title: 'Destinos de progreso en Slack', help: 'Canales internos aprobados que reciben el progreso de un proyecto o equipo. Los ítems restringidos solo publican un enlace salvo que el destino esté aprobado para contenido restringido y todos sus miembros sean miembros plenos del workspace.', scope: 'Alcance', project: 'Proyecto', team: 'Equipo', workspace: 'ID del workspace', channel: 'ID del canal', restricted: 'Aprobado para contenido restringido', milestones: 'Hitos', add: 'Guardar destino', remove: 'Quitar', empty: 'No hay destinos configurados.', saved: 'Destino guardado', milestoneNames: { REQUEST_RECEIVED: 'Solicitud recibida', REQUEST_ACCEPTED: 'Solicitud aceptada', REQUEST_REJECTED: 'Solicitud rechazada', REQUEST_RESOLVED: 'Solicitud resuelta', REQUEST_NEEDS_ATTENTION: 'Requiere atención', WORK_STARTED: 'Trabajo iniciado', WORK_BLOCKED: 'Trabajo bloqueado', WORK_DELIVERED: 'Trabajo entregado' } },
  },
  reports: {
    notes: {
      sourceNotConnected: '{{source}} no está conectado; sus valores son desconocidos, no cero.',
      sourceNotLive: 'Los datos de {{source}} vienen del modo {{mode}}, no son evidencia en vivo.',
      sourceNeverSynced: '{{source}} todavía no se sincronizó correctamente.',
      duplicatesIncluded: '{{count}} de estas solicitudes están marcadas como duplicadas.',
      reconsideredExcluded: '{{count}} decisiones reconsideradas en el período se excluyen de la tasa.',
      generationsAcrossRequests: '{{generations}} generaciones de cumplimiento en {{requests}} solicitudes distintas.',
      mixedBases: 'Bases de conclusión mezcladas ({{bases}}); elige una para comparar lo comparable.',
      partialCohorts: 'La ventana de seguimiento va desde cada primera conclusión hasta hoy; las cohortes recientes son parciales.',
      currentState: 'Estado actual al momento de la consulta; para esta métrica no se reconstruyen estados pasados.',
      rolledBack: '{{count}} despliegues revertidos se muestran por separado.',
      noIncidentSource: 'No hay una fuente de incidentes conectada; el tiempo de recuperación no está disponible y nunca se infiere de los despliegues.',
      activityNotProductivity: 'Las contribuciones son indicadores de actividad, no una medida de productividad.',
      prAttribution: 'La autoría se indica por pull request, no por línea. Las exclusiones de archivos generados solo aplican con datos completos de archivos.',
      captureDenominator: 'Denominador: pull requests observados en repositorios vinculados a proyectos; el trabajo fuera de la plataforma no tiene denominador.',
      providerErrors: '{{count}} llamadas al proveedor fallaron.',
    },
    filters: { any: 'Cualquiera', source: 'Origen', mine: 'Solo mis contribuciones' },
    chartLabel: 'Gráfico de {{metric}} por grupo',
    title: 'Reportes',
    subtitle: 'Métricas calculadas a partir de hechos registrados, con tu acceso aplicado. Los datos desconocidos nunca se muestran como cero.',
    metric: 'Métrica',
    period: 'Período',
    groupBy: 'Agrupar por',
    basis: 'Base de finalización',
    anyBasis: 'Todas las bases',
    firstOnly: 'Solo primera finalización',
    run: 'Ejecutar',
    export: 'Exportar CSV',
    exporting: 'Preparando exportación…',
    download: 'Descargar CSV',
    total: 'Total',
    count: 'Cantidad',
    unavailable: 'No disponible',
    median: 'Mediana',
    p85: 'Percentil 85',
    coverage: 'Cobertura',
    overlapping: 'Los grupos se superponen: una tarea puede estar en varios grupos, así que las filas no suman el total.',
    empty: 'No hay datos en este período.',
    tierLabel: 'Nivel',
    formula: 'Fórmula v{{version}}',
    watermark: 'Datos al {{time}}',
    definition: 'Definición',
    last7: 'Últimos 7 días',
    last30: 'Últimos 30 días',
    last90: 'Últimos 90 días',
    thisYear: 'Este año',
    from: 'Desde',
    to: 'Hasta',
    group: 'Grupo',
    value: 'Valor',
    ratio: '{{num}} de {{den}}',
    tiers: { GENERAL: 'General', TECHNICAL: 'Técnico', SENSITIVE: 'Sensible' },
    coverageStatus: { COMPLETE: 'Completa', PARTIAL: 'Parcial', UNKNOWN: 'Desconocida' },
    groups: { none: 'Sin agrupar', team: 'Equipo', project: 'Proyecto', type: 'Tipo', priority: 'Prioridad', week: 'Semana', month: 'Mes', repository: 'Repositorio', service: 'Servicio', person: 'Persona', outcome: 'Resultado', source: 'Origen' },
    metrics: {
      requests_created: { name: 'Solicitudes creadas', help: 'Solicitudes enviadas según fecha de envío.' },
      request_first_response: { name: 'Tiempo de primera respuesta', help: 'Desde el envío hasta la primera decisión humana de triage.' },
      request_acceptance_rate: { name: 'Tasa de aceptación', help: 'Primeras aceptaciones sobre aceptadas más rechazadas.' },
      requests_fulfilled: { name: 'Solicitudes resueltas', help: 'Solicitudes distintas resueltas en el período.' },
      request_lead_time: { name: 'Tiempo total de solicitud', help: 'Desde el envío hasta la resolución, por resolución.' },
      work_completed: { name: 'Trabajo completado', help: 'Ítems de trabajo distintos completados en el período.' },
      work_cycle_time: { name: 'Tiempo de ciclo', help: 'Desde el inicio hasta la finalización de cada episodio.' },
      work_in_progress: { name: 'Trabajo en curso', help: 'Ítems en curso, revisión o integrados ahora.' },
      blocked_time: { name: 'Tiempo bloqueado', help: 'Unión de intervalos bloqueados por ítem en el período.' },
      review_wait: { name: 'Espera de revisión', help: 'Desde el pedido de revisión hasta la decisión.' },
      integration_to_production: { name: 'De integración a producción', help: 'Desde la integración hasta la entrega verificada en producción.' },
      deployment_frequency: { name: 'Frecuencia de despliegue', help: 'Despliegues exitosos en producción.' },
      deployment_failure_rate: { name: 'Tasa de fallas de despliegue', help: 'Fallidos sobre exitosos más fallidos en producción.' },
      reopen_rate: { name: 'Tasa de reapertura', help: 'Trabajo completado que luego se reabrió.' },
      recovery_time: { name: 'Tiempo de recuperación', help: 'Requiere evidencia de incidentes; no disponible sin una fuente.' },
      contribution_activity: { name: 'Contribuciones', help: 'Contribuciones observadas de implementación, revisión y otras.' },
      code_additions: { name: 'Líneas agregadas', help: 'Adiciones reportadas por el proveedor en PRs fusionados.' },
      code_deletions: { name: 'Líneas eliminadas', help: 'Eliminaciones reportadas por el proveedor en PRs fusionados.' },
      capture_coverage: { name: 'Cobertura de captura', help: 'PRs observados vinculados a trabajo confirmado.' },
      requester_confirmation: { name: 'Confirmación del solicitante', help: 'Confirmado, persiste o sin respuesta por resolución.' },
      integrated_ai_usage: { name: 'Uso de IA', help: 'Tokens reportados por el proveedor para traducción e intake.' },
    },
  },
  attachments: {
    title: 'Archivos',
    add: 'Adjuntar archivos',
    empty: 'Todavía no hay archivos adjuntos.',
    uploading: 'Subiendo {{name}}…',
    scanning: 'Analizando malware',
    rejected: 'Rechazado',
    download: 'Descargar',
    remove: 'Quitar archivo',
    removed: 'Archivo quitado',
    tooLarge: '{{name}} supera los {{max}} MiB.',
    tooMany: 'Podés adjuntar como máximo {{max}} archivos a la vez.',
    uploaded: '{{count}} archivo(s) subido(s); estarán disponibles después del análisis de malware.',
    pendingNote: 'Los archivos quedan en cuarentena hasta que termine el análisis.',
    video: 'Video',
    view: 'Ver {{name}}',
    viewer: 'Visor de archivos',
    viewerHint: 'Usá las flechas para pasar de un archivo a otro y Escape para cerrar.',
    counter: '{{index}} de {{total}}',
    previous: 'Archivo anterior',
    next: 'Archivo siguiente',
    actualSize: 'Tamaño real',
    fitToScreen: 'Ajustar a la pantalla',
    openInNewTab: 'Abrir en una pestaña nueva',
    loadFailed: 'Este archivo no se puede mostrar acá. Descargalo para abrirlo en tu dispositivo.',
  },
  savedViews: {
    title: 'Vistas',
    mine: 'Mis vistas',
    sharedWithEveryone: 'Vistas compartidas',
    shared: 'Compartida',
    empty: 'Todavía no hay vistas guardadas.',
    saveCurrent: 'Guardar filtros actuales…',
    name: 'Nombre de la vista',
    shareLabel: 'Compartir con todos (solo los filtros; cada persona sigue viendo únicamente lo que puede acceder)',
    hint: 'Una vista guardada conserva los filtros, nunca los resultados.',
    saved: 'Vista guardada',
    deleted: 'Vista eliminada',
    delete: 'Eliminar vista',
  },
  slack: {
    modalTitle: 'Crear solicitud Vammo',
    reviewTitle: 'Revisar solicitud',
    next: 'Siguiente',
    submit: 'Enviar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    privacyNote: 'Solo vos ves este formulario. La solicitud es privada hasta que la envíes.',
    fields: { title: 'Título corto', problem: '¿Qué está pasando?', expected: '¿Qué debería pasar en cambio?', impact: '¿A quién afecta y cuánto? (dejalo vacío si no lo sabés)' },
    impactUnknown: 'Desconocido por ahora',
    duplicates: 'Solicitudes posiblemente relacionadas',
    submitted: 'Solicitud *{{key}}* enviada. Seguila acá: {{url}}',
    dmHelp: 'Escribime en un mensaje lo que necesitás y lo convierto en un borrador de solicitud Vammo que revisás antes de enviar. También podés usar /vammo-work o el atajo "Create Vammo request" en cualquier mensaje.',
    dmDraftReady: 'Preparé un borrador de solicitud: *{{title}}*. Revisalo, completalo y envialo:',
    dmReview: 'Revisar y enviar',
    linkPrompt: 'Para crear solicitudes desde Slack, vinculá tu cuenta de Slack con tu cuenta de Vammo (enlace privado, válido por 10 minutos): {{url}}',
    notLinked: 'Tu cuenta de Slack todavía no está vinculada. Ejecutá /vammo-work de nuevo.',
    expired: 'Este borrador ya no está disponible. Empezá de nuevo con /vammo-work.',
    required: 'Obligatorio',
    unavailable: 'No se pudo abrir el formulario. Probá de nuevo o usá la aplicación web.',
    progress: '*{{key}}* {{title}} — {{status}}\n{{url}}',
    progressLinkOnly: 'Se actualizó un ítem seguido. Abrilo en Vammo Work Tracker: {{url}}',
    linkTitle: 'Vincular cuenta de Slack',
    linkBody: '¿Vincular el usuario de Slack {{handle}} con tu cuenta de Vammo? Las solicitudes que crees desde Slack se registrarán a tu nombre.',
    linkConfirm: 'Vincular cuenta',
    linkDone: 'Tu cuenta de Slack quedó vinculada. Podés cerrar esta pestaña y ejecutar /vammo-work de nuevo.',
    linkInvalid: 'Este enlace venció o ya se usó. Ejecutá /vammo-work en Slack para obtener uno nuevo.',
  },
  notifications: {
    preferences: {
      title: 'Configuración de notificaciones',
      event: 'Evento',
      email: 'Email',
      browser: 'Navegador',
      immediate: 'Al instante',
      digest: 'Resumen diario',
      muted: 'Desactivado',
      on: 'Activado',
      off: 'Desactivado',
    },
    events: {
      REQUEST_RECEIVED: 'Recibimos tu solicitud',
      REQUEST_NEEDS_INFORMATION: 'Se necesita más información',
      REQUEST_ACCEPTED: 'Tu solicitud fue aceptada',
      REQUEST_REJECTED: 'Tu solicitud no fue aceptada',
      REQUEST_WORK_STARTED: 'Empezó el trabajo en tu solicitud',
      REQUEST_RESOLVED: 'Tu solicitud fue resuelta',
      REQUEST_NEEDS_ATTENTION: 'Una solicitud requiere atención',
      WORK_ASSIGNED: 'Trabajo asignado a ti',
      WORK_BLOCKED: 'Algo que sigues está bloqueado',
      COMMENT_ADDED: 'Nuevo comentario en algo que sigues',
      MENTIONED: 'Te mencionaron',
    },
    browser: {
      title: 'Notificaciones del navegador',
      enable: 'Activar en este navegador',
      disable: 'Desactivar en este navegador',
      test: 'Enviar una prueba',
      enabled: 'Las notificaciones del navegador están activadas',
      disabled: 'Las notificaciones están desactivadas en este navegador',
      testSent: 'Notificación de prueba enviada',
      testNone: 'Ningún navegador aceptó la prueba. Desactiva y vuelve a activar las notificaciones.',
      blockedHelp: 'Las notificaciones están bloqueadas para este sitio. Permítelas en la configuración del sitio del navegador (el ícono junto a la dirección).',
      devices: 'Navegadores que reciben notificaciones',
      unknownDevice: 'Navegador',
      forget: 'Olvidar',
      state: {
        unsupported: 'Este navegador no puede mostrar notificaciones. En iPhone y iPad, primero agrega el tracker a la pantalla de inicio.',
        blocked: 'Las notificaciones están bloqueadas para este sitio. Permítelas en la configuración del sitio del navegador y recarga la página.',
        off: 'Recibe asignaciones, menciones y novedades de lo que sigues, aunque el tracker esté cerrado.',
        on: 'Este navegador muestra tus notificaciones.',
      },
    },
  },
  push: {
    testTitle: 'Las notificaciones están activadas',
    testBody: 'Este navegador mostrará tus notificaciones de Vammo Work Tracker.',
  },
  email: {
    greeting: 'Hola {{name}},',
    open: 'Abrir en Vammo Work Tracker',
    footer: 'Recibes esto porque participas en este registro. Cambia las notificaciones en tu perfil.',
    actionConfirm: 'Sí, está resuelto',
    actionPersists: 'No, sigue pasando',
    actionComment: 'Dejar un comentario',
    confirmHint: 'Cuéntale al equipo si te funciona: abre la solicitud y elige "Sí, está resuelto" o "No, sigue pasando". Abrir este correo o enlace nunca registra una respuesta por sí solo.',
    digestSubject: 'Tu resumen de Vammo Work Tracker',
    layout: {
      kindRequest: 'Solicitud',
      kindWork: 'Trabajo',
      openRequest: 'Abrir la solicitud',
      openWork: 'Abrir el trabajo',
      openMyWork: 'Abrir Mi trabajo',
      manageNotifications: 'Gestionar tus notificaciones',
      actionNeeded: 'Requiere acción',
      update: 'Novedad',
      digestHeadline: 'Tu resumen del día',
      digestIntro: 'Esto pasó en los registros que sigues ({{count}}).',
    },
    events: {
      REQUEST_RECEIVED: { subject: '[{{key}}] Recibimos tu solicitud', headline: 'Recibimos tu solicitud', body: 'Tu solicitud "{{title}}" fue recibida y espera triage.' },
      REQUEST_NEEDS_INFORMATION: { subject: '[{{key}}] Necesitamos más información', headline: 'El equipo necesita más información', body: 'El equipo necesita más información sobre "{{title}}".' },
      REQUEST_ACCEPTED: { subject: '[{{key}}] Tu solicitud fue aceptada', headline: 'Tu solicitud fue aceptada', body: 'Tu solicitud "{{title}}" fue aceptada. Aceptar no es una fecha de entrega; te avisaremos cuando empiece el trabajo.' },
      REQUEST_REJECTED: { subject: '[{{key}}] Tu solicitud no fue aceptada', headline: 'Tu solicitud no fue aceptada', body: 'Tu solicitud "{{title}}" no fue aceptada. Ábrela para leer el motivo.' },
      REQUEST_WORK_STARTED: { subject: '[{{key}}] Empezó el trabajo', headline: 'Empezó el trabajo en tu solicitud', body: 'Empezó el trabajo en "{{title}}".' },
      REQUEST_RESOLVED: { subject: '[{{key}}] Resuelta — confírmalo, por favor', headline: 'Tu solicitud fue resuelta', body: 'El equipo resolvió "{{title}}".' },
      REQUEST_NEEDS_ATTENTION: { subject: '[{{key}}] Requiere atención', headline: 'Una solicitud necesita tu atención', body: 'El solicitante indica que "{{title}}" sigue ocurriendo, o se perdió la entrega requerida.' },
      WORK_ASSIGNED: { subject: '[{{key}}] Asignado a ti', headline: 'Tienes trabajo nuevo asignado', body: '"{{title}}" te fue asignado.' },
      WORK_BLOCKED: { subject: '[{{key}}] Bloqueado', headline: 'Algo que sigues está bloqueado', body: '"{{title}}" está bloqueado.' },
      WORK_DONE: { subject: '[{{key}}] Terminado', headline: 'Algo que sigues está terminado', body: '"{{title}}" está terminado.' },
      COMMENT_ADDED: { subject: '[{{key}}] Nuevo comentario', headline: 'Nuevo comentario', body: 'Hay un nuevo comentario en "{{title}}".' },
      MENTIONED: { subject: '[{{key}}] Te mencionaron', headline: 'Te mencionaron', body: 'Te mencionaron en "{{title}}".' },
    },
  },
  tutorial: {
    title: 'Tutorial',
    subtitle: 'Conecta tu asistente de IA, pide lo que necesitas, planifica el trabajo con tu equipo y deja que el trabajo de código se registre solo.',
    toc: 'En esta página',
    copy: 'Copiar',
    copied: 'Copiado',
    copyFailed: 'No se pudo copiar; selecciona el texto y cópialo a mano.',
    footer: 'Todo lo que hagas aquí, tú o tu asistente, se ejecuta con tus propios permisos.',
    connect: {
      title: 'Conecta tu asistente de IA',
      intro: 'El tracker es un servidor MCP. Una vez conectado, tu asistente puede crear solicitudes, crear y actualizar trabajo y registrar lo que hace, siempre como tú y con tus permisos. Inicias sesión con tu cuenta de Vammo; no hay ninguna API key que copiar.',
      serverUrl: 'URL del servidor',
      claudeDesktop: {
        name: 'Claude Desktop',
        summary: 'La app de Claude para Windows y macOS, y claude.ai en la web.',
        s1: 'Abre **Settings → Connectors** y elige **Add custom connector**.',
        s2: 'Llámalo `Vammo Work Tracker` y pega la URL del servidor:',
        s3: 'Elige **Connect**. Se abre el inicio de sesión de Vammo en el navegador; inicia sesión y aprueba el acceso solicitado.',
        s4: 'En un chat nuevo, abre el menú de herramientas (**+**) y verifica que **Vammo Work Tracker** esté activado.',
        s5: 'Opcional: pega estas instrucciones en un proyecto de Claude para que siempre trabaje con el tracker:',
        instructions: 'Usa el conector Vammo Work Tracker para solicitudes y trabajo. Llama primero a get_context. Para crear una solicitud, llama a prepare_request con lo que te describa, muéstrame el borrador y los campos faltantes, y llama a submit_request solo después de que lo confirme. Cuando una solicitud mía se resuelva, pregúntame si quedó solucionada y registra confirm_request. Nunca inventes hechos, prioridades ni estados de despliegue.',
      },
      claudeCode: {
        name: 'Claude Code (terminal)',
        summary: 'Claude en tu terminal, tu IDE o la pestaña Code de la app de escritorio.',
        s1: 'Agrega el servidor una sola vez; `--scope user` lo deja disponible en todos tus repositorios:',
        s2: 'Dentro de Claude Code ejecuta `/mcp`, selecciona `vammo` y elige **Authenticate**. Inicia sesión con tu cuenta de Vammo en el navegador.',
        s3: 'Instala la skill de seguimiento para que el trabajo de código se registre automáticamente: copia `agent-integrations/claude/claude-code/skills/vammo-work-tracking/` del repositorio del tracker en `~/.claude/skills/`.',
      },
      chatgptDesktop: {
        name: 'ChatGPT Desktop',
        summary: 'La app de ChatGPT y chatgpt.com. Los conectores que agregas en la web también aparecen en la app de escritorio.',
        s1: 'En ChatGPT abre **Settings → Apps & Connectors → Advanced settings** y activa **Developer mode**.',
        s2: 'De vuelta en **Apps & Connectors**, elige **Create**, llámalo `Vammo Work Tracker`, elige autenticación **OAuth** y pega la URL del servidor:',
        s3: 'Elige **Create**, inicia sesión con tu cuenta de Vammo y aprueba el acceso solicitado.',
        s4: 'En un chat, abre el menú **+**, elige **Developer mode** y selecciona el conector de Vammo. ChatGPT te pide confirmación antes de cada cambio que hace.',
        note: 'Los nombres de los menús cambian según el plan y la versión de la app. En los planes Business y Enterprise puede que un administrador del workspace tenga que habilitar el modo desarrollador o publicar el conector primero.',
      },
      codex: {
        name: 'Codex (ChatGPT terminal)',
        summary: 'La CLI de OpenAI Codex y la extensión de Codex para el IDE.',
        s1: 'Agrega el servidor a `~/.codex/config.toml`:',
        s2: 'Inicia sesión; el navegador abre el inicio de sesión de Vammo:',
        s3: 'Instala la skill de seguimiento: copia `agent-integrations/codex/skills/vammo-work-tracking/` del repositorio del tracker en `~/.codex/skills/`.',
      },
      verifyTitle: 'Verifica la conexión',
      verify: 'Pídele a tu asistente: “Llama a get_context en el servidor vammo.” Debería responder con tu nombre y tus permisos. La sesión se renueva sola hasta por 7 días; después vuelves a iniciar sesión.',
      adminNote: 'Si tu organización de Claude o ChatGPT bloquea los conectores personalizados, pide al responsable del workspace que permita esta URL.',
    },
    request: {
      title: 'Pide una nueva funcionalidad',
      intro: 'Cualquiera puede pedir una funcionalidad o reportar un problema. No necesitas saber cómo construirlo: describe qué necesitas y por qué. Cada solicitud recibe una clave `REQ-`.',
      web: { title: 'Desde la web', body: 'Elige **Nueva solicitud** o presiona `R`. La pestaña **Guiado** te hace preguntas en lenguaje simple; la pestaña **Formulario** te deja completar los campos directamente. Los borradores son privados hasta que los envías.' },
      assistant: { title: 'Desde Claude o ChatGPT', body: 'Di, por ejemplo: “Crea una solicitud: los conductores quieren ver su historial de swaps en la app.” El asistente prepara un borrador, te muestra lo que falta y solo lo envía cuando confirmas.' },
      slack: { title: 'Desde Slack', body: 'Ejecuta `/vammo-work` o usa el atajo **Create Vammo request** sobre cualquier mensaje. La primera vez, Slack te envía un enlace privado para conectar tu cuenta.' },
      tipsTitle: 'Qué hace buena a una solicitud',
      t1: 'Qué pasa hoy y qué debería pasar en cambio.',
      t2: 'A quién afecta y cuánto importa; “todavía no lo sé” es una respuesta válida.',
      t3: 'Capturas, enlaces o ejemplos como adjuntos.',
      after: 'Triage acepta la solicitud y la vincula a trabajo, y recibes avisos a medida que avanza. Cuando se entrega, te preguntan si resolvió tu problema.',
    },
    team: {
      title: 'Crea tareas para tu equipo',
      intro: 'Los ítems de trabajo (claves `VAM-`) son las tareas que ejecuta un equipo. Una solicitud describe una necesidad; un ítem de trabajo describe qué se va a hacer al respecto.',
      s1: 'Elige **Nuevo ítem de trabajo** o presiona `C`.',
      s2: 'Escribe un título corto y elige el tipo: funcionalidad, bug, mantenimiento, investigación, etc.',
      s3: 'En **Responsable**, elige el equipo (o **Personal** para tu propio trabajo). Opcionalmente asigna una persona, un proyecto y una prioridad.',
      s4: 'Agrega criterios de aceptación: los resultados observables que indican que la tarea está terminada.',
      s5: 'Para atender una solicitud, ábrela y elige **Nuevo ítem de trabajo** desde ahí, así quien la pidió sigue el avance automáticamente.',
      s6: 'Sigue el avance en **Trabajo** como lista o tablero, filtrando por equipo, proyecto o responsable.',
      assistantTitle: 'O pídeselo a tu asistente',
      assistant: '“Crea una tarea para el equipo de Plataforma para agregar un filtro al historial de swaps, prioridad alta.” El asistente te dice qué va a crear y te pide confirmación antes.',
    },
    notifications: {
      title: 'Activa las notificaciones',
      intro: 'Entérate cuando tus solicitudes avanzan, cuando te asignan trabajo o cuando alguien te menciona, sin tener el tracker abierto. Todo aparece además en la lista de notificaciones dentro del tracker.',
      open: 'Configurar notificaciones',
      s1: 'Abre **Configuración → Notificaciones**, o elige la campana arriba a la derecha.',
      s2: 'En **Notificaciones del navegador**, elige **Activar en este navegador** y acepta cuando el navegador pida permiso.',
      s3: 'Elige **Enviar una prueba** para comprobarlo. Repítelo en cada computadora o teléfono donde quieras recibirlas; la misma tarjeta lista tus navegadores para que olvides los viejos.',
      s4: 'En **Configuración de notificaciones**, elige para cada evento: email **Al instante**, **Resumen diario** o **Desactivado**, y navegador **Activado** o **Desactivado**.',
      browser: { title: 'Navegador', body: 'Avisos emergentes en este dispositivo, aunque la pestaña del tracker esté cerrada. Se activan por navegador y por dispositivo.' },
      email: { title: 'Email', body: 'Activado por defecto para tus solicitudes, asignaciones y menciones. **Resumen diario** agrupa las novedades del día en un solo email.' },
      slack: { title: 'Slack', body: 'Las solicitudes creadas desde Slack reciben su avance en el mismo hilo. Los administradores pueden aprobar un canal por proyecto o equipo para recibir sus novedades.' },
      troubleTitle: 'Si no te llegan las notificaciones',
      blocked: 'Si el navegador las bloqueó, permite las notificaciones en la configuración del sitio (el ícono junto a la dirección) y recarga la página.',
      iphone: 'En iPhone o iPad, primero agrega el tracker a la pantalla de inicio (**Compartir → Agregar a inicio**) y actívalas desde ese ícono.',
      missing: 'Si no ves **Notificaciones del navegador** en Configuración, todavía no están habilitadas en este entorno; el email y la lista dentro de la app siguen funcionando.',
    },
    tracking: {
      title: 'El trabajo de código se registra solo',
      intro: 'No necesitas actualizar tickets a mano. Cuando programas con un asistente conectado o abres pull requests, el tracker registra el trabajo y lo vincula a la tarea correcta.',
      f1: { title: 'Empiezas una tarea', body: 'Con la skill instalada, el asistente registra tu objetivo, repositorio y rama. Reutiliza la tarea que nombras (por ejemplo `VAM-42`) o la que ya está vinculada a tu rama o sesión, y crea una nueva solo cuando nada coincide.' },
      f2: { title: 'Se registran los hitos', body: 'El avance, las verificaciones que realmente ejecutó, los bloqueos y los pull requests se agregan a la tarea a medida que avanza la sesión. Un archivo pequeño dentro de `.git/` recuerda el vínculo entre sesiones y nunca se commitea.' },
      f3: { title: 'GitHub lo confirma', body: 'Un pull request cuyo título, rama o descripción menciona una clave como `VAM-42`, o cuya rama coincide con una sesión de agente, se vincula automáticamente. Asocia el repositorio a un proyecto; además de los webhooks, el tracker reconcilia cada 15 minutos.' },
      f4: { title: 'La entrega se verifica', body: 'Cuando todos los pull requests vinculados se mergean, el trabajo de producción pasa a integrado. Llega a **Terminado** solo cuando el tracker observa el cambio desplegado en producción, no porque se mergeó un PR o terminó una sesión.' },
      keyTipTitle: 'Consejo: pon la clave en la rama',
      keyTip: 'Una rama como `feat/VAM-42-swap-history`, o `VAM-42` en el título del pull request, vincula el trabajo con certeza, incluso sin asistente.',
      ambiguousTitle: 'Cuando la coincidencia es dudosa',
      ambiguous: 'Los títulos parecidos son solo sugerencias. Si varias tareas podrían coincidir, el asistente te muestra las candidatas y te pregunta. Si el trabajo tiene que seguir mientras tanto, crea un ítem provisional que aparece en la bandeja de excepciones para confirmarlo o fusionarlo; no se pierde nada.',
    },
  },
  time: {
    justNow: 'justo ahora',
  },
};
